import { MetricsFieldName, useAdvAccountPerformanceReportTimeSeriesQuery } from '~/generated/graphql'
import React, { useEffect } from 'react'
import { ErrorAlert } from '~/components/common/ErrorAlert'
import { MetricsKey, MetricType } from './index'
import { MetricsChart } from './MetricsChart'
import { eventPush } from '~/lib/gtmHelper'
import { DateTime } from 'luxon'
import { validateReportDates } from '~/lib/feature/report'

type CampaignPerformanceChartProps = {
  metrics: MetricType
  firstMetric: MetricsKey
  secondMetric: MetricsKey
  startDate: string
  endDate: string
  haveCustomized: boolean
}

export const AdvAccountMetricsChart: React.FC<CampaignPerformanceChartProps> = ({
  metrics,
  firstMetric,
  secondMetric,
  startDate,
  endDate,
  haveCustomized,
}) => {
  const formattedStartDate = DateTime.fromISO(startDate)
  const formattedEndDate = DateTime.fromISO(endDate)
  const { data, error } = useAdvAccountPerformanceReportTimeSeriesQuery({
    variables: {
      input: {
        startedAt: startDate,
        endedAt: endDate,
        series: [metrics[firstMetric], metrics[secondMetric]],
      },
    },
    skip: !validateReportDates(formattedStartDate, formattedEndDate).isValid,
  })

  useEffect(() => {
    if (!data) return

    const countValues = (metric: MetricsFieldName) =>
      data.advAccountPerformanceReportTimeSeries.find((item) => item.name === metric)?.value.length

    eventPush('view_performance_report_graph', {
      started: startDate,
      ended: endDate,
      graph_Y1_kind: metrics[firstMetric],
      graph_Y2_kind: metrics[secondMetric],
      graph_Y1_count: countValues(metrics[firstMetric]) ?? 0,
      graph_Y2_count: countValues(metrics[secondMetric]) ?? 0,
      has_customized: haveCustomized ? 1 : 0,
      aggregation_unit_kind: 'adv_account',
    })
  }, [data, endDate, firstMetric, haveCustomized, metrics, secondMetric, startDate])

  if (error) return <ErrorAlert title="キャンペーンデータの取得に失敗しました" error={error} />

  const performanceTimeSeries =
    data?.advAccountPerformanceReportTimeSeries.map((item) => ({
      name: item.name,
      value: item.value.map((v) => {
        return { date: v.date, value: v.value ?? 0 }
      }),
    })) ?? []

  return (
    <MetricsChart
      data={performanceTimeSeries}
      metrics={metrics}
      firstMetric={firstMetric}
      secondMetric={secondMetric}
    />
  )
}
